/**
 * エラー処理の統一化のための型定義
 */

/**
 * エラーカテゴリ
 * エラーの種類を分類し、適切な処理を行うため
 */
export enum ErrorCategory {
  /** ファイル操作関連のエラー（読み込み、書き込み、パース等） */
  FILE_IO = 'file_io',
  
  /** ネットワーク関連のエラー（API通信、タイムアウト等） */
  NETWORK = 'network',
  
  /** データ処理関連のエラー（計算、変換、フィルタリング等） */
  DATA_PROCESSING = 'data_processing',
  
  /** ユーザー操作関連のエラー（入力値不正、操作ミス等） */
  USER_ACTION = 'user_action',
  
  /** 権限関連のエラー（ファイルアクセス権限等） */
  PERMISSION = 'permission',
  
  /** システムエラー（メモリ不足、予期しないエラー等） */
  SYSTEM = 'system',
  
  /** 検証エラー（データ検証、型チェック等） */
  VALIDATION = 'validation'
}

/**
 * エラーの深刻度
 * ユーザーへの表示方法と処理の継続可否を決定
 */
export enum ErrorSeverity {
  /** 情報レベル（処理は正常完了、参考情報） */
  INFO = 'info',
  
  /** 警告レベル（処理は完了したが注意が必要） */
  WARNING = 'warning',
  
  /** エラーレベル（処理は失敗したが、アプリは継続可能） */
  ERROR = 'error',
  
  /** 致命的エラー（アプリの継続が困難） */
  CRITICAL = 'critical'
}

/**
 * アプリケーション共通のエラークラス
 */
export class AppError extends Error {
  /** エラーカテゴリ */
  public readonly category: ErrorCategory;
  
  /** エラーの深刻度 */
  public readonly severity: ErrorSeverity;
  
  /** エラーから回復可能かどうか */
  public readonly recoverable: boolean;
  
  /** ユーザー向けのメッセージ（日本語OK） */
  public readonly userMessage?: string;
  
  /** エラーの詳細情報（デバッグ用） */
  public readonly details?: Record<string, unknown>;
  
  /** エラーが発生したタイムスタンプ */
  public readonly timestamp: number;
  
  /** エラーのユニークID（トラッキング用） */
  public readonly id: string;

  constructor(
    message: string,  // 技術的なメッセージ（英語、ログ用）
    category: ErrorCategory,
    severity: ErrorSeverity = ErrorSeverity.ERROR,
    options?: {
      recoverable?: boolean;
      userMessage?: string;
      details?: Record<string, unknown>;
    }
  ) {
    super(message);
    this.name = 'AppError';
    this.category = category;
    this.severity = severity;
    this.recoverable = options?.recoverable ?? (severity !== ErrorSeverity.CRITICAL);
    this.userMessage = options?.userMessage;
    this.details = options?.details;
    this.timestamp = Date.now();
    this.id = `${this.timestamp}-${Math.random().toString(36).substr(2, 9)}`;
    
    // スタックトレースを保持
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, AppError);
    }
  }

  /**
   * エラーをJSON形式に変換（ログ出力用）
   */
  toJSON(): Record<string, unknown> {
    return {
      id: this.id,
      name: this.name,
      message: this.message,
      category: this.category,
      severity: this.severity,
      recoverable: this.recoverable,
      userMessage: this.userMessage,
      details: this.details,
      timestamp: this.timestamp,
      stack: this.stack
    };
  }

  /**
   * ユーザー向けのメッセージを取得
   * userMessageが設定されていない場合は、カテゴリに応じたデフォルトメッセージを返す
   */
  getUserMessage(): string {
    if (this.userMessage) {
      return this.userMessage;
    }

    // カテゴリに応じたデフォルトメッセージ
    switch (this.category) {
      case ErrorCategory.FILE_IO:
        return 'File operation failed. Please check the file and try again.';
      case ErrorCategory.NETWORK:
        return 'Network error occurred. Please check your connection.';
      case ErrorCategory.DATA_PROCESSING:
        return 'Data processing failed. Please check your data.';
      case ErrorCategory.USER_ACTION:
        return 'Invalid operation. Please check your input.';
      case ErrorCategory.PERMISSION:
        return 'Permission denied. Please check your access rights.';
      case ErrorCategory.VALIDATION:
        return 'Validation failed. Please check your input values.';
      case ErrorCategory.SYSTEM:
      default:
        return 'An unexpected error occurred. Please try again.';
    }
  }

  /**
   * エラーが特定のカテゴリかどうかをチェック
   */
  isCategory(category: ErrorCategory): boolean {
    return this.category === category;
  }

  /**
   * エラーが特定の深刻度以上かどうかをチェック
   */
  isSeverityOrHigher(severity: ErrorSeverity): boolean {
    const severityOrder = {
      [ErrorSeverity.INFO]: 0,
      [ErrorSeverity.WARNING]: 1,
      [ErrorSeverity.ERROR]: 2,
      [ErrorSeverity.CRITICAL]: 3
    };
    return severityOrder[this.severity] >= severityOrder[severity];
  }
}

/**
 * エラーハンドリングのためのヘルパー関数
 */

/**
 * 通常のErrorオブジェクトをAppErrorに変換
 */
export function toAppError(
  error: unknown,
  category: ErrorCategory = ErrorCategory.SYSTEM,
  userMessage?: string
): AppError {
  if (error instanceof AppError) {
    return error;
  }

  if (error instanceof Error) {
    return new AppError(
      error.message,
      category,
      ErrorSeverity.ERROR,
      {
        userMessage,
        details: {
          originalError: error.name,
          stack: error.stack
        }
      }
    );
  }

  // エラーオブジェクトでない場合
  return new AppError(
    String(error),
    category,
    ErrorSeverity.ERROR,
    {
      userMessage,
      details: {
        originalError: error
      }
    }
  );
}

/**
 * ファイルエラー用のヘルパー
 */
export function createFileError(
  message: string,
  filepath: string,
  operation: 'read' | 'write' | 'parse' | 'delete',
  originalError?: unknown
): AppError {
  const userMessages = {
    read: `Could not read file: ${filepath}`,
    write: `Could not write to file: ${filepath}`,
    parse: `Could not parse file: ${filepath}`,
    delete: `Could not delete file: ${filepath}`
  };

  return new AppError(
    message,
    ErrorCategory.FILE_IO,
    ErrorSeverity.ERROR,
    {
      userMessage: userMessages[operation],
      details: {
        filepath,
        operation,
        originalError
      }
    }
  );
}

/**
 * データ処理エラー用のヘルパー
 */
export function createDataProcessingError(
  message: string,
  operation: string,
  datasetId?: string,
  originalError?: unknown
): AppError {
  return new AppError(
    message,
    ErrorCategory.DATA_PROCESSING,
    ErrorSeverity.ERROR,
    {
      userMessage: `Data processing failed during ${operation}`,
      details: {
        operation,
        datasetId,
        originalError
      }
    }
  );
}

/**
 * ユーザー入力エラー用のヘルパー
 */
export function createValidationError(
  message: string,
  field: string,
  value: unknown,
  expectedFormat?: string
): AppError {
  const userMessage = expectedFormat
    ? `Invalid value for ${field}. Expected format: ${expectedFormat}`
    : `Invalid value for ${field}`;

  return new AppError(
    message,
    ErrorCategory.VALIDATION,
    ErrorSeverity.WARNING,
    {
      recoverable: true,
      userMessage,
      details: {
        field,
        value,
        expectedFormat
      }
    }
  );
}